import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { platformStats, faqItems } from '@/mocks/home';

const milestones = [
  { year: '2018', title: '平台上线', desc: '茄子视频正式成立，致力于为用户提供免费高清在线观影服务。' },
  { year: '2019', title: '用户突破百万', desc: '凭借优质的内容和流畅的体验，注册用户突破100万。' },
  { year: '2020', title: '4K画质上线', desc: '率先支持4K超高清画质，引领在线观影体验新标杆。' },
  { year: '2022', title: '用户突破千万', desc: '平台日均活跃用户突破1000万，成为行业领先的视频平台。' },
  { year: '2024', title: '智能推荐上线', desc: '基于AI的智能推荐系统上线，为用户提供个性化观影体验。' },
  { year: '2026', title: '全面升级', desc: '平台完成全面技术升级，支持智能缓冲和跨设备无缝切换。' },
];

const advantages = [
  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '采用先进的加密技术，保障您的观影隐私和安全。' },
  { icon: 'ri-flashlight-line', title: '极速流畅', desc: '智能缓冲技术确保视频播放不卡顿，带来极致流畅体验。' },
  { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K超高清画质，让每一帧画面都纤毫毕现。' },
  { icon: 'ri-device-line', title: '全平台支持', desc: '电脑、平板、手机无缝切换，随时随地畅享高清影视。' },
  { icon: 'ri-customer-service-2-line', title: '贴心服务', desc: '7x24小时客服支持，及时解决您在使用中遇到的任何问题。' },
  { icon: 'ri-loop-right-line', title: '每日更新', desc: '内容团队每日更新影视资源，热播剧集2小时内上线。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9mu1om8mbnljin59rl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed as Record<string, string> | null)?.message || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://qiezi.video/#organization",
        "name": "茄子视频",
        "url": "https://qiezi.video/",
        "description": "茄子视频提供高清免费在线观看，不缓冲流畅播放电视剧全集、电影大全",
        "email": "contact@qiezi.video",
        "telephone": "400-888-6666",
        "foundingDate": "2018"
      },
      {
        "@type": "WebPage",
        "@id": "https://qiezi.video/about#webpage",
        "url": "https://qiezi.video/about",
        "name": "关于茄子视频 - 品牌介绍与联系方式",
        "description": "茄子视频成立于2018年，是国内领先的高清免费在线视频平台。了解我们的品牌故事、发展历程和联系方式。",
        "inLanguage": "zh-CN",
        "isPartOf": { "@id": "https://qiezi.video/#website" },
        "about": { "@id": "https://qiezi.video/#organization" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "茄子视频是否需要注册才能观看？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "不需要。茄子视频完全免费，无需注册即可在线观看所有内容，真正做到即点即看。"
            }
          },
          {
            "@type": "Question",
            "name": "视频播放卡顿怎么办？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "茄子视频采用智能缓冲技术，可根据您的网络状况自动调整清晰度。如遇卡顿，建议尝试切换至较低清晰度，或检查您的网络连接。"
            }
          },
          {
            "@type": "Question",
            "name": "支持哪些设备观看？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "茄子视频支持电脑、平板、手机等所有主流设备观看，无需安装任何插件或APP，打开浏览器即可畅享高清影视。"
            }
          },
          {
            "@type": "Question",
            "name": "视频资源多久更新一次？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "我们的内容团队每天更新影视资源，热播剧集一般在播出后2小时内上线，确保您不错过任何精彩内容。"
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium whitespace-nowrap">关于我们</span>
          </div>
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 font-bold mb-4">关于茄子视频</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              茄子视频成立于2018年，是国内领先的高清免费在线视频平台。我们致力于为用户提供海量优质的影视内容，
              以流畅的播放体验和超清画质赢得了数千万用户的信赖与喜爱。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center p-4 md:p-5 bg-white rounded-xl">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-primary-100 mb-3">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story - Timeline */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold text-center mb-10">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className={`${index % 2 === 0 ? 'md:pr-10' : 'md:pl-10'}`}>
                        <span className="text-2xl font-bold text-primary-500 whitespace-nowrap">{item.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className="flex-1 md:hidden pl-10">
                      <span className="text-xl font-bold text-primary-500 whitespace-nowrap">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1.5 leading-relaxed">{item.desc}</p>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold text-center mb-10">为什么选择茄子视频</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {advantages.map((item) => (
                <div key={item.title} className="bg-white rounded-xl p-5 md:p-6 flex gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className={`${item.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-1.5 whitespace-nowrap">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold text-center mb-10">常见问题</h2>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white rounded-xl overflow-hidden">
                  <div className="px-5 py-4 md:px-6 md:py-4.5">
                    <span className="text-sm md:text-base font-medium text-foreground-900">{item.question}</span>
                    <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold text-center mb-4">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">如有任何问题或建议，欢迎通过以下方式联系我们</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <div className="bg-background-100 rounded-xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">电子邮箱</div>
                  <div className="text-sm font-medium text-foreground-900 whitespace-nowrap">contact@qiezi.video</div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-phone-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">客服热线</div>
                  <div className="text-sm font-medium text-foreground-900 whitespace-nowrap">400-888-6666</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm font-medium text-foreground-700">姓名</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-700">邮箱</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="subject" className="text-sm font-medium text-foreground-700">主题</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-sm font-medium text-foreground-700">消息内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入您的消息内容（不超过500字）"
                  ></textarea>
                </div>
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors self-start"
                >
                  发送消息
                </button>

                {formStatus === 'success' && (
                  <div className="mt-2 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                    <i className="ri-check-line mr-1.5"></i>
                    消息发送成功！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
    </>
  );
}